import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { toSlug } from '../../lib/slug';
import { Organization } from '../../types';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import DataTable from '../../components/ui/DataTable';
import Modal from '../../components/ui/Modal';
import Badge from '../../components/ui/Badge';

export default function BrandsPage() {
  const [brands, setBrands] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({ name: '' });
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [editData, setEditData] = useState<{ name: string; type: 'AGENCY' | 'BRAND' }>({ name: '', type: 'BRAND' });
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  useEffect(() => {
    loadBrands();
  }, []);

  const loadBrands = async () => {
    try {
      const response = await api.get('/organizations');
      const brandOrgs = response.data.filter((org: Organization) => org.type === 'BRAND');
      setBrands(brandOrgs);
    } catch (error) {
      console.error('Failed to load brands:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const response = await api.post('/organizations', {
        name: formData.name,
        type: 'BRAND',
      });
      setBrands([response.data, ...brands]);
      await queryClient.invalidateQueries({ queryKey: ['agency', 'dashboard'] });
      setShowCreateModal(false);
      setFormData({ name: '' });
    } catch (error) {
      console.error('Failed to create brand:', error);
    }
  };

  const openEdit = (org: Organization) => {
    setSelectedOrg(org);
    setEditData({ name: org.name, type: org.type });
    setShowEditModal(true);
  };

  const handleEdit = async () => {
    if (!selectedOrg) return;
    try {
      const response = await api.put(`/organizations/${selectedOrg.id}`, {
        name: editData.name,
        type: editData.type,
      });
      setBrands(brands.map((c) => (c.id === selectedOrg.id ? response.data : c)));
      setShowEditModal(false);
      setSelectedOrg(null);
    } catch (error) {
      console.error('Failed to update brand:', error);
    }
  };

  const columns = [
    {
      header: 'Name',
      accessor: (row: Organization) => (
        <div className="font-medium text-glass-text">{row.name}</div>
      ),
    },
    {
      header: 'Type',
      accessor: (row: Organization) => (
        <Badge variant="secondary">{row.type}</Badge>
      ),
    },
    {
      header: 'Created',
      accessor: (row: Organization) => new Date(row.createdAt).toLocaleDateString(),
    },
    {
      header: 'Actions',
      accessor: (row: Organization) => (
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={(e) => { e.stopPropagation(); openEdit(row); }}>
            <Pencil className="w-4 h-4 mr-2" />
            Edit
          </Button>
          <Button variant="outline" onClick={(e) => { e.stopPropagation(); navigate(`/agency/brands/${toSlug(row.name, row.id)}/team`); }}>
            <Users className="w-4 h-4 mr-2" />
            Team
          </Button>
        </div>
      ),
      className: 'text-right'
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[2.25rem] font-semibold tracking-tightest text-glass-text">Brands</h1>
          <p className="text-glass-text-2 mt-1">Manage brand clients your agency works with</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add brand
        </Button>
      </div>

      <div className="glass-card overflow-hidden">
        <DataTable
          data={brands}
          columns={columns}
          isLoading={loading}
          onRowClick={(org) => navigate(`/agency/brands/${toSlug(org.name, org.id)}`)}
          emptyMessage="No brands yet. Add your first brand to get started!"
        />
      </div>

      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Add brand"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button onClick={handleCreate}>Create</Button>
          </div>
        }
      >
        <div>
          <label className="block text-sm font-medium text-glass-text mb-1">Brand name *</label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData({ name: e.target.value })}
            placeholder="e.g., Nike Malaysia"
          />
        </div>
      </Modal>

      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit brand"
        footer={
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
            <Button onClick={handleEdit}>Save Changes</Button>
          </div>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-glass-text mb-1">Brand name *</label>
            <Input
              value={editData.name}
              onChange={(e) => setEditData({ ...editData, name: e.target.value })}
              placeholder="e.g., Nike Malaysia"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-glass-text mb-1">Type *</label>
            <select
              value={editData.type}
              onChange={(e) => setEditData({ ...editData, type: e.target.value as 'AGENCY' | 'BRAND' })}
              className="glass-input w-full px-3 py-2 h-10 text-sm"
            >
              <option value="BRAND">Brand</option>
              <option value="AGENCY">Agency</option>
            </select>
          </div>
        </div>
      </Modal>
    </div>
  );
}
